import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { ApiService } from 'app/core/api/api.service';
import { BehaviorSubject, Observable, tap, catchError, throwError } from 'rxjs';

export interface UserPageParams {
    page: number;
    limit: number;
    sortBy?: string;
}

@Injectable({
    providedIn: 'root',
})
export class TeamService {
    private _data: BehaviorSubject<any> = new BehaviorSubject(null);
    private _apiService = inject(ApiService);
    private dataStore: { users: any[] } = { users: [] };

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for data
     */
    get data$(): Observable<any> {
        return this._data.asObservable();
    }

    get(): any {
        return this.dataStore.users;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all users
     */
    getUsers(params: UserPageParams = { page: 1, limit: 10 }): Observable<any> {
        return this.getUsersPage('/users/super-admin-all-users', params).pipe(
            tap((users: any) => {
                this._data.next(users);
            })
        );
    }
    /**
     * Get all players (Super Admin)
     */
    getPlayers(params: UserPageParams = { page: 1, limit: 10 }): Observable<any> {
        return this.getUsersPage('/users/super-admin-players', params).pipe(
            tap((players: any) => {
                this._data.next(players);
            })
        );
    }

    /**
     * Get all admins (Super Admin)
     */
    getAdmins(params: UserPageParams = { page: 1, limit: 10 }): Observable<any> {
        return this.getUsersPage('/users/super-admin-admins', params).pipe(
            tap((admins: any) => {
                this._data.next(admins);
            })
        );
    }

    private getUsersPage(endpoint: string, params: UserPageParams): Observable<any> {
        const query = new URLSearchParams({
            page: String(params.page),
            limit: String(params.limit),
            ...(params.sortBy ? { sortBy: params.sortBy } : {}),
        });

        return this._apiService.get(`${endpoint}?${query.toString()}`);
    }

    /**
     * Create a new manager
     * @param managerData The manager data to create
     */
    createManager(managerData: any): Observable<any> {
        return this._apiService.post('admin/managers', managerData).pipe(
            tap((newManager: any) => {
                // Optional: Update local data
            })
        );
    }

    /**
     * Delete a manager
     * @param managerId The ID of the manager to delete
     */
    deleteManager(managerId: string): Observable<any> {
        return this._apiService.delete(`admin/managers/${managerId}`).pipe(
            tap(() => {
                // Optional: Update local data
            })
        );
    }

    /**
     * Bulk update managers with different values for each
     * @param updates Array of manager updates with managerId and fields to update
     */
    updateManagersBulk(
        managers: Array<{
            managerId: string;
            role?: string;
            email?: string;
            venueIds?: string | string[];
        }>
    ): Observable<any> {
        return this._apiService.put('admin/managers', { managers }).pipe(
            tap((response: any) => {
                // Optional: Update local data
            })
        );
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Super Admin User Operations
    // -----------------------------------------------------------------------------------------------------

    /**
     * Update a user as super admin
     * @param userId The ID of the user to update
     * @param updateData The data to update (name, phone, country, language)
     */
    updateUserAsSuperAdmin(
        userId: string,
        updateData: {
            name?: string;
            phone?: string;
            country?: string;
            language?: string;
        }
    ): Observable<any> {
        return this._apiService.patch(`/users/super-admin/${userId}`, updateData).pipe(
            tap((updatedUser: any) => {
                // Update the user in the local data store if it exists
                if (this.dataStore.users) {
                    const index = this.dataStore.users.findIndex((user) => user._id === userId);
                    if (index !== -1) {
                        this.dataStore.users[index] = {
                            ...this.dataStore.users[index],
                            ...updatedUser,
                        };
                        this._data.next(this.dataStore.users);
                    }
                }
            }),
            catchError((error: HttpErrorResponse) => {
                // Extract error message from response
                const errorMessage =
                    error.error?.message ||
                    error.error?.error?.message ||
                    error.message ||
                    'An error occurred while updating user';
                return throwError(() => new Error(errorMessage));
            })
        );
    }

    /**
     * Delete a user as super admin
     * @param userId The ID of the user to delete
     * @param reason Optional reason for deletion
     */
    deleteUserAsSuperAdmin(userId: string, reason?: string): Observable<any> {
        const body = reason ? { reason } : {};

        return this._apiService.delete(`/users/super-admin/${userId}`, body).pipe(
            tap(() => {
                // Remove the user from the local data store if it exists
                if (this.dataStore.users) {
                    this.dataStore.users = this.dataStore.users.filter(
                        (user) => user._id !== userId
                    );
                    this._data.next(this.dataStore.users);
                }
            }),
            catchError((error: HttpErrorResponse) => {
                // Extract error message from response
                const errorMessage =
                    error.error?.message ||
                    error.error?.error?.message ||
                    error.message ||
                    'An error occurred while deleting user';
                return throwError(() => new Error(errorMessage));
            })
        );
    }
}
